import {
    AdminContentBlock,
    AdminLandingPage,
    AdminLandingPageLink,
    AdminContentSection,
    ApiContentBlock,
    AdminLPButton,
    collectSectionProductLinkHashes,
    isProductTargetedButton,
    STOREFRONT_PRODUCTS_HASH_OPTIONS,
} from '@storefronts/common-types'
import { mergeUnattachedProductLinks } from './mergeLandingPageLinks'

/** Flattens a content block's tabs into an AdminContentSection array. */
export function sectionsFromContent(
    content: ApiContentBlock | AdminContentBlock | null | undefined,
): AdminContentSection[] {
    return (content?.tabs?.flatMap((tab) => tab.sections) ??
        []) as AdminContentSection[]
}

export function collectStorefrontSectionLinkHashes(
    sections: AdminContentSection[],
    button?: AdminLPButton | null,
): string[] {
    return collectSectionProductLinkHashes(
        sections,
        STOREFRONT_PRODUCTS_HASH_OPTIONS,
        isProductTargetedButton(button) ? button.linkHash : null,
    )
}

export function appendStorefrontProducts(
    landingPage: AdminLandingPage,
    incomingLinks: AdminLandingPageLink[],
): AdminLandingPage {
    if (incomingLinks.length === 0) {
        return landingPage
    }

    const landingPageLinks = mergeUnattachedProductLinks(
        landingPage.landingPageLinks ?? [],
        incomingLinks,
    )
    const attachedHashes = new Set(
        landingPageLinks.map((link) => link.linkHash),
    )
    const existingSectionLinks = landingPage.sectionLandingPageLinks
    const sectionLandingPageLinks = existingSectionLinks?.filter(
        (link) => !attachedHashes.has(link.linkHash),
    )

    return {
        ...landingPage,
        landingPageLinks,
        ...(existingSectionLinks
            ? {
                  sectionLandingPageLinks:
                      sectionLandingPageLinks &&
                      sectionLandingPageLinks.length > 0
                          ? sectionLandingPageLinks
                          : undefined,
              }
            : {}),
    }
}

/**
 * Replaces Storefront Products. Removed rows that are still referenced by
 * sections or the floating button stay on `sectionLandingPageLinks` so the
 * editor can resolve them without nested section lpLink copies.
 */
export function replaceStorefrontProducts(
    landingPage: AdminLandingPage,
    selectedLinks: AdminLandingPageLink[],
    referencedHashes: readonly string[],
): AdminLandingPage {
    const selectedHashes = new Set(selectedLinks.map((link) => link.linkHash))
    const referenced = new Set(referencedHashes)
    const removedStillReferenced = (landingPage.landingPageLinks ?? []).filter(
        (link) =>
            !selectedHashes.has(link.linkHash) && referenced.has(link.linkHash),
    )
    const retainedSection = (landingPage.sectionLandingPageLinks ?? []).filter(
        (link) => !selectedHashes.has(link.linkHash),
    )
    const sectionLandingPageLinks = mergeUnattachedProductLinks(
        retainedSection,
        removedStillReferenced,
    )

    return {
        ...landingPage,
        landingPageLinks: selectedLinks,
        sectionLandingPageLinks:
            sectionLandingPageLinks.length > 0
                ? sectionLandingPageLinks
                : undefined,
    }
}

/**
 * Section- and button-referenced products (per STOREFRONT_PRODUCTS_HASH_OPTIONS)
 * missing from landingPageLinks, minus `excludeHashes` (typically the
 * products the user has explicitly removed from Storefront Products).
 */
export function missingStorefrontProductHashes(
    landingPage: Pick<AdminLandingPage, 'landingPageLinks' | 'button'>,
    sections: AdminContentSection[],
    excludeHashes: Set<string> = new Set(),
): string[] {
    const attached = new Set(
        (landingPage.landingPageLinks ?? []).map((link) => link.linkHash),
    )
    return collectStorefrontSectionLinkHashes(
        sections,
        landingPage.button,
    ).filter((hash) => !attached.has(hash) && !excludeHashes.has(hash))
}

/**
 * Append-only reconciliation: adds the products from
 * {@link missingStorefrontProductHashes}, resolved against the loaded
 * catalog. Pure and synchronous — returns the same landing page instance
 * when nothing is missing.
 *
 * Without `excludeHashes` the sync would silently re-attach any hash a
 * section still references, defeating the removal.
 */
export function syncStorefrontProductsFromCatalog(
    landingPage: AdminLandingPage,
    sections: AdminContentSection[],
    catalogLinks: AdminLandingPageLink[],
    excludeHashes: Set<string> = new Set(),
): AdminLandingPage {
    const missingHashes = missingStorefrontProductHashes(
        landingPage,
        sections,
        excludeHashes,
    )
    if (missingHashes.length === 0) return landingPage

    const catalogByHash = new Map(
        catalogLinks.map((link) => [link.linkHash, link]),
    )
    const toAppend = missingHashes.flatMap((hash) => {
        const link = catalogByHash.get(hash)
        return link ? [link] : []
    })

    return appendStorefrontProducts(landingPage, toAppend)
}
